import { SerializedStyles } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Text } from '@frontend/design-system';
import { formatFieldLabel } from '../../../../../helpers';
import { ESignPreview } from '../fields/esign-field';
import { MaskedSummary } from '../fields/payment-on-file-field';
import { hasSavedPaymentMethod } from '../fields/payment-on-file-field/payment-field.helpers';
import { PdfReviewPreview } from '../fields/pdf-field';
import { RichTextField } from '../fields/rich-text-field/rich-text-field';
import { getFieldOptions } from '../helpers';
import { getFieldAttachments, MediaAttachment } from '../media-upload';
import { isESignSigned, parseESignValue } from '../signature/esign-value';
import { DynamicFormField } from '../types';
import {
  reviewCardImageStyle,
  reviewImageStyle,
  reviewImagesRowStyle,
  reviewLabelStyle,
  reviewRowFullWidthStyle,
  reviewRowStyle,
  reviewValueStyle,
} from './review-field.styles';

interface ReviewFieldProps {
  field: DynamicFormField;
  attachments: Record<string, MediaAttachment>;
}

const NOT_ANSWERED = '-';
// Answers longer than this wrap awkwardly in a half-width column, so they get the full row instead.
const FULL_WIDTH_CHAR_THRESHOLD = 40;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const asStringArray = (value: unknown): string[] => (Array.isArray(value) ? (value as string[]) : []);

/** Read-only label/value row. Reused by every field type that just needs its answer displayed as text. */
const ReviewRow = ({ label, value }: { label: string; value: string }) => {
  const displayValue = value || NOT_ANSWERED;
  const isFullWidth = displayValue.length > FULL_WIDTH_CHAR_THRESHOLD;

  return (
    <div css={[reviewRowStyle, isFullWidth && reviewRowFullWidthStyle]}>
      <Text size='small' color='light' css={reviewLabelStyle}>
        {label}
      </Text>
      <Text weight='bold' css={reviewValueStyle}>
        {displayValue}
      </Text>
    </div>
  );
};

interface MediaThumbnailsProps {
  label: string;
  fieldKeys: string[];
  attachments: Record<string, MediaAttachment>;
  imageStyle?: SerializedStyles;
}

const MediaThumbnails = ({ label, fieldKeys, attachments, imageStyle = reviewImageStyle }: MediaThumbnailsProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const images = fieldKeys
    .flatMap((key) => getFieldAttachments(attachments, key))
    .filter((attachment) => !!attachment.previewUrl);

  if (images.length === 0) {
    return <ReviewRow label={label} value={NOT_ANSWERED} />;
  }

  return (
    <div css={[reviewRowStyle, reviewRowFullWidthStyle]}>
      <Text size='small' color='light' css={reviewLabelStyle}>
        {label}
      </Text>
      <div css={reviewImagesRowStyle}>
        {images.map((attachment) => (
          <img key={attachment.id} src={attachment.previewUrl} alt={t('Uploaded file')} css={imageStyle} />
        ))}
      </div>
    </div>
  );
};

/**
 * Single read-only renderer for a submitted field's answer. Most field types resolve to a plain
 * label/value row; a few (card capture, image upload) need a richer preview instead of raw text.
 */
export const ReviewField = ({ field, attachments }: ReviewFieldProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const label = formatFieldLabel(field.label, false);
  const value = field.value;

  switch (field.meta.type) {
    // These field types don't carry a reviewable answer (actions, content, or not yet implemented for filling).
    case 'button':
    case 'text-with-button':
    case 'signature':
      return null;

    // Static content, not an answer: render it as authored so the review matches what was shown while filling.
    case 'richText':
      if (!value) return null;
      return (
        <div css={[reviewRowStyle, reviewRowFullWidthStyle]}>
          <RichTextField fieldKey={field.key} htmlContent={value as string} />
        </div>
      );

    case 'eSign': {
      const signature = parseESignValue(value);
      if (!isESignSigned(signature)) return <ReviewRow label={label} value={t('No Signatures Added')} />;
      return (
        <div css={[reviewRowStyle, reviewRowFullWidthStyle]}>
          <Text size='small' color='light' css={reviewLabelStyle}>
            {label}
          </Text>
          <ESignPreview value={signature} fieldKey={field.key} />
        </div>
      );
    }

    case 'pdf': {
      const title = label || t('Document');
      return (
        <div css={[reviewRowStyle, reviewRowFullWidthStyle]}>
          <Text size='small' color='light' css={reviewLabelStyle}>
            {title}
          </Text>
          <PdfReviewPreview field={field} title={title} />
        </div>
      );
    }

    case 'cardOnFile':
    case 'achOnFile': {
      if (!hasSavedPaymentMethod(field.meta.type, value)) {
        return (
          <ReviewRow
            label={label}
            value={field.meta.type === 'cardOnFile' ? t('No card added!') : t('No ACH added!')}
          />
        );
      }
      return (
        <div css={[reviewRowStyle, reviewRowFullWidthStyle]}>
          <Text size='small' color='light' css={reviewLabelStyle}>
            {label}
          </Text>
          <MaskedSummary type={field.meta.type} value={value} />
        </div>
      );
    }

    case 'imageUpload':
      return <MediaThumbnails label={label} fieldKeys={[field.key]} attachments={attachments} />;

    case 'cardCapture':
      return (
        <MediaThumbnails
          label={label}
          fieldKeys={[`${field.key}.front`, `${field.key}.back`]}
          attachments={attachments}
          imageStyle={reviewCardImageStyle}
        />
      );

    case 'address': {
      const address = asRecord(value);
      const line = [address.address1, address.address2].filter(Boolean).join(', ');
      return <ReviewRow label={label} value={line} />;
    }

    case 'insuranceVerification': {
      const insurance = asRecord(value);
      const memberId = insurance.memberId ? `#${insurance.memberId}` : '';
      const summary = [insurance.payerName, memberId].filter(Boolean).join(' \u2022 ');
      return <ReviewRow label={label} value={summary} />;
    }

    case 'checkbox':
      return <ReviewRow label={label} value={value ? t('Yes') : t('No')} />;

    case 'checklist':
    case 'multiselect-dropdown': {
      const options = getFieldOptions(field.options);
      const selected = asStringArray(value);
      const labels = options.filter((option) => selected.includes(option.value)).map((option) => option.label);
      return <ReviewRow label={label} value={labels.join(', ')} />;
    }

    case 'categorizedChecklist': {
      const selected = asStringArray(value);
      const options = (field.categories ?? []).flatMap((category) => category.options ?? []);
      const labels = options
        .filter((option) => option.value && selected.includes(option.value))
        .map((option) => option.label ?? '');
      return <ReviewRow label={label} value={labels.join(', ')} />;
    }

    case 'radio':
    case 'select':
    case 'optionswitch': {
      const options = getFieldOptions(field.options);
      const selectedLabel = options.find((option) => option.value === value)?.label;
      return <ReviewRow label={label} value={selectedLabel ?? String(value ?? '')} />;
    }

    default:
      return <ReviewRow label={label} value={String(value ?? '')} />;
  }
};
